import {GPT_MAX_FILE_BYTES, GPT_MAX_FILES} from "@repo/ui/gailgpt/files";
import {AssistantModel} from "@/types/ai";
import {resolveEffectiveModelAlias} from "@/utils/ai/modelCatalog";
import {FormatDataSize} from "./library/formatters";

export {getFileIconName as getFileTypeIcon} from "@/utils/fileIcons";

export interface FileUploadConfig {
   enabled: boolean;
   acceptedFileTypes: string;
   maxFileSize: number;
   maxFileCount: number;
   totalMaxFileSize: number;
}

export enum FileRejectionCode {
   UPLOADS_NOT_SUPPORTED = "UPLOADS_NOT_SUPPORTED",
   FILE_TOO_LARGE = "FILE_TOO_LARGE",
   TYPE_NOT_SUPPORTED = "TYPE_NOT_SUPPORTED",
   PDF_TOO_MANY_PAGES = "PDF_TOO_MANY_PAGES",
   PDF_VALIDATION_FAILED = "PDF_VALIDATION_FAILED",
}

export interface FileCompatibilityResult {
   compatible: boolean;
   reason?: string;
   code?: FileRejectionCode;
}

/**
 * Count the number of pages in a PDF file using PDF.js (pdfjs-dist).
 *
 * PDF.js is Mozilla's production PDF engine and handles all standard PDF
 * structures (compressed object streams, hybrid XRef tables, etc.) that
 * pdf-lib@1.x fails on.
 *
 * pdfjs-dist is lazy-loaded so server-side code that only imports constants
 * from this module doesn't pull in the browser-oriented PDF engine.
 */
export const countPdfPages = async (file: File): Promise<number> => {
   try {
      const pdfjs = await import("pdfjs-dist");
      if (!pdfjs.GlobalWorkerOptions.workerSrc) {
         pdfjs.GlobalWorkerOptions.workerSrc = new URL(
            "pdfjs-dist/build/pdf.worker.min.mjs",
            import.meta.url,
         ).toString();
      }
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({data: arrayBuffer}).promise;
      return pdf.numPages;
   } catch (error) {
      if (error instanceof Error && error.name === "PasswordException") {
         throw new Error("Password-protected PDF files are not supported");
      }
      console.error("Error counting PDF pages:", error);
      throw new Error("Unable to count PDF pages");
   }
};

/**
 * Check if the assistant is using a Gemini model
 */
export const isGeminiAssistant = (assistantModel?: string): boolean => {
   return resolveEffectiveModelAlias(assistantModel)?.startsWith("gemini") ?? false;
};

/** Maximum PDF page limits per model provider (source: provider documentation). */
const GEMINI_3_MAX_PDF_PAGES = 3000;
const GEMINI_2_MAX_PDF_PAGES = 1000;
const DEFAULT_MAX_PDF_PAGES = 1000;
export const GAILGPT_MAX_PDF_PAGES = 1000;

export const getMaxPdfPages = (assistantModel?: string): number => {
   const alias = resolveEffectiveModelAlias(assistantModel);
   if (!alias) return DEFAULT_MAX_PDF_PAGES;
   if (alias.startsWith("gemini-3")) return GEMINI_3_MAX_PDF_PAGES;
   if (alias.startsWith("gemini")) return GEMINI_2_MAX_PDF_PAGES;
   return DEFAULT_MAX_PDF_PAGES;
};

/**
 * Truncate long file names while preserving extension
 */
export const truncateFileName = (name: string, maxLength: number = 30): string => {
   if (name.length <= maxLength) return name;

   const lastDotIndex = name.lastIndexOf(".");

   // If there's no extension, just truncate the name
   if (lastDotIndex === -1) {
      return name.substring(0, maxLength - 3) + "...";
   }

   const extension = name.substring(lastDotIndex);
   const nameWithoutExt = name.substring(0, lastDotIndex);
   const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3) + "...";

   return `${truncatedName}${extension}`;
};

/**
 * Format file size in human readable format
 */
export const formatFileSize = FormatDataSize;

/**
 * Check if a file is compatible with the given file upload configuration
 */
export const isFileCompatible = (
   file: File,
   fileUploadConfig?: FileUploadConfig
): FileCompatibilityResult => {
   if (!fileUploadConfig?.enabled) {
      return {compatible: false, reason: "File uploads not supported", code: FileRejectionCode.UPLOADS_NOT_SUPPORTED};
   }

   const maxFileSize = fileUploadConfig.maxFileSize || getDefaultFileSize();

   if (file.size > maxFileSize) {
      return {
         compatible: false,
         reason: `File too large (${formatFileSize(file.size)} > ${formatFileSize(maxFileSize)})`,
         code: FileRejectionCode.FILE_TOO_LARGE,
      };
   }

   if (fileUploadConfig.acceptedFileTypes && fileUploadConfig.acceptedFileTypes !== "*/*") {
      const allowedTypes = fileUploadConfig.acceptedFileTypes.split(",").map((t) => t.trim());

      const isAllowed = allowedTypes.some((allowedType) => {
         if (allowedType.startsWith(".")) {
            return file.name.toLowerCase().endsWith(allowedType.toLowerCase());
         } else if (allowedType.includes("/*")) {
            return file.type.startsWith(allowedType.replace("/*", "/"));
         } else {
            return file.type === allowedType;
         }
      });

      if (!isAllowed) {
         return {
            compatible: false,
            reason: `File type '${file.type}' not supported`,
            code: FileRejectionCode.TYPE_NOT_SUPPORTED,
         };
      }
   }

   return {compatible: true};
};

/**
 * Check if a file is compatible with the given file upload configuration (async version with PDF page validation)
 */
export const isFileCompatibleAsync = async (
   file: File,
   fileUploadConfig?: FileUploadConfig,
   assistantModel?: string,
   maxPdfPages?: number
): Promise<FileCompatibilityResult> => {
   const basicCheck = isFileCompatible(file, fileUploadConfig);
   if (!basicCheck.compatible) {
      return basicCheck;
   }

   if (file.type === "application/pdf") {
      const maxPages = maxPdfPages ?? getMaxPdfPages(assistantModel);
      try {
         const pageCount = await countPdfPages(file);
         if (pageCount > maxPages) {
            return {
               compatible: false,
               reason: `PDF exceeds maximum ${maxPages} pages`,
               code: FileRejectionCode.PDF_TOO_MANY_PAGES,
            };
         }
      } catch {
         return {
            compatible: false,
            reason: "Unable to validate PDF page count",
            code: FileRejectionCode.PDF_VALIDATION_FAILED,
         };
      }
   }

   return {compatible: true};
};

interface CategorizedFiles {
   tooLargeFiles: File[];
   unsupportedTypeFiles: File[];
   unsupportedUploadFiles: File[];
   tooManyPagesFiles: File[];
}

interface AlertOptions {
   showAlerts?: boolean;
   setAlert?: (alert: {
      status: "success" | "error" | "warning" | "info";
      message: string;
      secondsToClose?: 4 | 10 | 30;
   }) => void;
   t?: (key: string, options?: any) => string;
   maxPdfPages?: number;
}

/**
 * Categorize rejected files by rejection reason
 */
const categorizeRejectedFiles = (
   rejectedFiles: {file: File; reason: string; code?: FileRejectionCode}[]
): CategorizedFiles => {
   const categorized: CategorizedFiles = {
      tooLargeFiles: [],
      unsupportedTypeFiles: [],
      unsupportedUploadFiles: [],
      tooManyPagesFiles: [],
   };

   rejectedFiles.forEach(({file, code, reason}) => {
      switch (code) {
         case FileRejectionCode.UPLOADS_NOT_SUPPORTED:
            categorized.unsupportedUploadFiles.push(file);
            break;
         case FileRejectionCode.FILE_TOO_LARGE:
            categorized.tooLargeFiles.push(file);
            break;
         case FileRejectionCode.TYPE_NOT_SUPPORTED:
            categorized.unsupportedTypeFiles.push(file);
            break;
         case FileRejectionCode.PDF_TOO_MANY_PAGES:
         case FileRejectionCode.PDF_VALIDATION_FAILED:
            categorized.tooManyPagesFiles.push(file);
            break;
         default:
            // Legacy fallback for callers not yet passing codes
            if (reason.includes("too large")) categorized.tooLargeFiles.push(file);
            else if (reason.includes("not supported")) categorized.unsupportedTypeFiles.push(file);
      }
   });

   return categorized;
};

/**
 * Show appropriate alerts based on categorized files
 */
const showFileValidationAlerts = (
   categorized: CategorizedFiles,
   fileUploadConfig: {maxFileSize?: number} | undefined,
   options: AlertOptions
) => {
   if (!options.showAlerts || !options.setAlert || !options.t) {
      return;
   }

   if (categorized.tooManyPagesFiles.length > 0) {
      options.setAlert({
         status: "warning",
         message: options.t("pdfTooManyPages", {
            count: categorized.tooManyPagesFiles.length,
            maxPages: options.maxPdfPages ?? DEFAULT_MAX_PDF_PAGES,
            defaultValue: `{{count}} PDF file(s) exceed maximum {{maxPages}} pages`,
         }),
         secondsToClose: 4,
      });
   } else if (categorized.tooLargeFiles.length > 0) {
      const maxSize = fileUploadConfig?.maxFileSize || getDefaultFileSize();
      options.setAlert({
         status: "warning",
         message: options.t("filesTooLarge", {
            count: categorized.tooLargeFiles.length,
            maxSize: formatFileSize(maxSize),
            defaultValue: `{{count}} file(s) too large. Maximum size: ${formatFileSize(maxSize)}`,
         }),
         secondsToClose: 4,
      });
   } else if (categorized.unsupportedTypeFiles.length > 0) {
      options.setAlert({
         status: "warning",
         message: options.t("filesUnsupportedType", {
            count: categorized.unsupportedTypeFiles.length,
         }),
         secondsToClose: 4,
      });
   } else if (categorized.unsupportedUploadFiles.length > 0) {
      options.setAlert({
         status: "warning",
         message: options.t("filesNotSupported", {
            count: categorized.unsupportedUploadFiles.length,
         }),
         secondsToClose: 4,
      });
   }
};

export const validateFilesWithFeedback = (
   files: File[],
   fileUploadConfig?: FileUploadConfig,
   options?: AlertOptions
): {validFiles: File[]; rejectedFiles: {file: File; reason: string; code?: FileRejectionCode}[]} => {
   const rejectedFiles: {file: File; reason: string; code?: FileRejectionCode}[] = [];

   const validFiles = files.filter((file) => {
      const result = isFileCompatible(file, fileUploadConfig);
      if (!result.compatible && result.reason) {
         rejectedFiles.push({file, reason: result.reason, code: result.code});
         return false;
      }
      return true;
   });

   if (options) {
      const categorized = categorizeRejectedFiles(rejectedFiles);
      showFileValidationAlerts(categorized, fileUploadConfig, options);
   }

   return {validFiles, rejectedFiles};
};

/**
 * Async version of validateFilesWithFeedback that includes PDF page validation
 */
export const validateFilesWithFeedbackAsync = async (
   files: File[],
   fileUploadConfig?: FileUploadConfig,
   assistantModel?: string,
   options?: AlertOptions,
   maxPdfPages?: number
): Promise<{
   validFiles: File[];
   rejectedFiles: {file: File; reason: string; code?: FileRejectionCode}[];
}> => {
   const rejectedFiles: {file: File; reason: string; code?: FileRejectionCode}[] = [];
   const validFiles: File[] = [];

   for (const file of files) {
      const result = await isFileCompatibleAsync(file, fileUploadConfig, assistantModel, maxPdfPages);

      if (!result.compatible && result.reason) {
         rejectedFiles.push({file, reason: result.reason, code: result.code});
      } else {
         validFiles.push(file);
      }
   }

   if (options) {
      const categorized = categorizeRejectedFiles(rejectedFiles);
      showFileValidationAlerts(categorized, fileUploadConfig, {
         ...options,
         maxPdfPages: maxPdfPages ?? getMaxPdfPages(assistantModel),
      });
   }

   return {validFiles, rejectedFiles};
};

export const getIncompatibleFiles = (
   files: File[],
   fileUploadConfig?: FileUploadConfig
): Array<{file: File; reason: string; code?: FileRejectionCode; index: number}> => {
   if (!fileUploadConfig?.enabled) {
      return files.map((file, index) => ({
         file,
         reason: "This assistant doesn't support file uploads",
         code: FileRejectionCode.UPLOADS_NOT_SUPPORTED,
         index,
      }));
   }

   const incompatibleFiles: Array<{file: File; reason: string; code?: FileRejectionCode; index: number}> = [];

   files.forEach((file, index) => {
      const result = isFileCompatible(file, fileUploadConfig);
      if (!result.compatible && result.reason) {
         incompatibleFiles.push({file, reason: result.reason, code: result.code, index});
      }
   });

   return incompatibleFiles;
};

/**
 * Async version of getIncompatibleFiles that includes PDF page validation
 */
export const getIncompatibleFilesAsync = async (
   files: File[],
   fileUploadConfig?: FileUploadConfig,
   assistantModel?: string,
   maxPdfPages?: number
): Promise<Array<{file: File; reason: string; code?: FileRejectionCode; index: number}>> => {
   if (!fileUploadConfig?.enabled) {
      return files.map((file, index) => ({
         file,
         reason: "This assistant doesn't support file uploads",
         code: FileRejectionCode.UPLOADS_NOT_SUPPORTED,
         index,
      }));
   }

   const incompatibleFiles: Array<{file: File; reason: string; code?: FileRejectionCode; index: number}> = [];

   for (let index = 0; index < files.length; index++) {
      const file = files[index];
      const result = await isFileCompatibleAsync(file, fileUploadConfig, assistantModel, maxPdfPages);
      if (!result.compatible && result.reason) {
         incompatibleFiles.push({file, reason: result.reason, code: result.code, index});
      }
   }

   return incompatibleFiles;
};

const convertMimeTypesToAcceptString = (mimeTypes: string[]): string => {
   if (!mimeTypes || mimeTypes.length === 0) {
      return "*/*";
   }
   return mimeTypes.join(",");
};

export const getFileUploadConfigFromAssistant = (assistant: {
   fileUpload?: {
      enabled: boolean;
      allowedMimeTypes?: string[];
      maxFileSizeBytes?: number;
      maxFileCount?: number;
      totalMaxFileSizeBytes?: number;
   };
}): FileUploadConfig => {
   if (assistant?.fileUpload) {
      const {fileUpload} = assistant;
      return {
         enabled: fileUpload.enabled,
         acceptedFileTypes: convertMimeTypesToAcceptString(fileUpload.allowedMimeTypes ?? []),
         maxFileSize: fileUpload.maxFileSizeBytes ?? 0,
         maxFileCount: fileUpload.maxFileCount ?? 0,
         totalMaxFileSize: fileUpload.totalMaxFileSizeBytes ?? 0,
      };
   }
   return {
      enabled: false,
      acceptedFileTypes: "",
      maxFileSize: 0,
      maxFileCount: 0,
      totalMaxFileSize: 0,
   };
};

/**
 * MIME type to file extension mapping
 */
const mimeTypeMap = {
   "text/plain": ".txt",
   "text/html": ".html",
   "text/csv": ".csv",
   "text/markdown": ".md",
   wordprocessingml: ".docx",
   spreadsheetml: ".xlsx",
   presentationml: ".pptx",
   "ms-excel": ".xls",
   docx: ".docx",
   xlsx: ".xlsx",
   pptx: ".pptx",
   xls: ".xls",
   pdf: ".pdf",
   jpeg: ".jpg",
   jpg: ".jpg",
   png: ".png",
   gif: ".gif",
   webp: ".webp",
   svg: ".svg",
   csv: ".csv",
   zip: ".zip",
   rar: ".rar",
   "7z": ".7z",
} as const;

/**
 * Helper functions for MIME type extension matching
 */
const getMimeTypeExtension = {
   exactMatch: (mimeType: string): string => {
      return mimeTypeMap[mimeType as keyof typeof mimeTypeMap] || "";
   },

   partialMatch: (mimeType: string): string => {
      for (const [pattern, extension] of Object.entries(mimeTypeMap)) {
         if (mimeType.includes(pattern)) {
            return extension;
         }
      }
      return "";
   },

   genericPattern: (mimeType: string): string => {
      const parts = mimeType.split("/");
      if (parts.length === 2 && parts[1] && !parts[1].includes("+")) {
         return `.${parts[1]}`;
      }
      return "";
   },
};

/**
 * Get file extension from MIME type
 */
export const getFileExtensionFromMimeType = (mimeType: string): string => {
   if (!mimeType) return "";

   const lowerMimeType = mimeType.toLowerCase();

   return (
      getMimeTypeExtension.exactMatch(lowerMimeType) ||
      getMimeTypeExtension.partialMatch(lowerMimeType) ||
      getMimeTypeExtension.genericPattern(lowerMimeType)
   );
};

/**
 * Get file extension from MIME type without the dot and uppercased
 */
export const getFileExtensionFromMimeTypeWithoutDot = (mimeType: string): string => {
   return getFileExtensionFromMimeType(mimeType).replace(".", "").toUpperCase();
};

export const ONE_MB = 1024 * 1024;
export const FIVE_MB = 5 * ONE_MB;
export const TEN_MB = 10 * ONE_MB;
export const TWENTY_MB = 20 * ONE_MB;
export const THIRTY_TWO_MB = 32 * ONE_MB;
export const ONE_HUNDRED_MB = 100 * ONE_MB;

/**
 * Shared file-upload overrides for GailGPT chat (100 MB per file, 1 000-page PDF limit).
 *
 * These override the assistant config because the backend currently returns stale
 * or incorrect maxFileSizeBytes values for some assistants. Once the assistant
 * config is reliable, these should be removed in favour of server-driven limits.
 */
export const GAILGPT_FILE_LIMITS = {
   maxFileSize: GPT_MAX_FILE_BYTES,
   totalMaxFileSize: GPT_MAX_FILE_BYTES,
   maxPdfPages: GAILGPT_MAX_PDF_PAGES,
} as const;

/** Per-model file size overrides. */
const MODEL_FILE_SIZE_LIMITS: Partial<Record<AssistantModel, number>> = {
   "gpt-6-astra": THIRTY_TWO_MB,
   "gpt-5.6-sol": THIRTY_TWO_MB,
   "gpt-5.6-terra": THIRTY_TWO_MB,
   "gpt-5.6-luna": THIRTY_TWO_MB,
   "gpt-5.5": THIRTY_TWO_MB,
   "gpt-5.4": THIRTY_TWO_MB,
};

/** Merge GailGPT file-size overrides into an existing file-upload config. */
export const applyGailGptLimits = <T extends Record<string, unknown>>(
   config: T,
   model?: string
): T & {maxFileSize: number; totalMaxFileSize: number} => {
   const resolvedModel = resolveEffectiveModelAlias(model);
   const modelLimit = resolvedModel
      ? MODEL_FILE_SIZE_LIMITS[resolvedModel as AssistantModel]
      : undefined;
   const maxFileSize = modelLimit ?? GAILGPT_FILE_LIMITS.maxFileSize;
   return {
      ...config,
      maxFileSize,
      totalMaxFileSize: maxFileSize,
   };
};

export const getDefaultFileSize = () => {
   return GPT_MAX_FILE_BYTES;
};

export const getDefaultFileCount = () => {
   return GPT_MAX_FILES;
};

/**
 * Whether `fileName` already ends with a dot-extension matching `extension`.
 * Used by the artifact card and panel to hide the redundant extension pill
 * when the file name already conveys the format (e.g. "report.xlsx" + "XLSX").
 * Case-insensitive on both sides.
 */
export const hasExtensionSuffix = (fileName: string, extension: string): boolean =>
   fileName.toLowerCase().endsWith(`.${extension.toLowerCase()}`);

/**
 * Derive a short uppercase file-extension label from a file name and MIME type.
 * Prefers the actual file extension when it is short enough (<=5 chars),
 * otherwise falls back to heuristics based on the MIME type.
 */
export const getFileExtension = (fileName: string, mimeType: string): string => {
   const ext = fileName.split(".").pop()?.toUpperCase();
   if (ext && ext.length <= 5) return ext;
   if (mimeType === "application/pdf") return "PDF";
   if (mimeType.startsWith("image/")) return mimeType.split("/")[1]?.toUpperCase() ?? "IMG";
   if (mimeType.startsWith("audio/")) return mimeType.split("/")[1]?.toUpperCase() ?? "AUDIO";
   if (mimeType.startsWith("video/")) return mimeType.split("/")[1]?.toUpperCase() ?? "VIDEO";
   if (mimeType.includes("word")) return "DOCX";
   // Cover both modern OOXML ("presentation"/"spreadsheet") and legacy
   // binary Office MIME types (application/vnd.ms-powerpoint / ms-excel)
   // so filenames without extensions still render a useful label.
   if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "PPTX";
   if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "XLSX";
   if (mimeType === "application/json") return "JSON";
   if (mimeType.startsWith("text/")) return "TXT";
   return "FILE";
};

/**
 * Stable, cheap-to-compute identity for a base64 artifact payload. Same name +
 * length can collide across distinct files, so we mix in a 32-bit polynomial
 * (Java `String.hashCode`-style, multiplier 31) hash over a prefix+suffix
 * sample of the base64. Sampling keeps the cost O(1) in payload size, and the
 * result is deterministic across renders.
 */
export const computeBinaryArtifactId = (fileName: string, base64: string): string => {
   const SAMPLE = 256;
   const sample =
      base64.length <= SAMPLE * 2 ? base64 : base64.slice(0, SAMPLE) + base64.slice(-SAMPLE);
   let hash = 0;
   for (let i = 0; i < sample.length; i++) {
      hash = Math.imul(hash, 31) + (sample.codePointAt(i) ?? 0);
   }
   return `binary:${fileName}:${base64.length}:${(hash >>> 0).toString(36)}`;
};

export {
   MIME_PDF,
   MIME_IMAGE,
   MIME_DOCUMENT,
   MIME_CSV,
   MIME_SPREADSHEET,
   MIME_PRESENTATION,
   MIME_GPT_DEFAULT,
   GPT_EXTENSIONS,
   GPT_MAX_FILES,
   GPT_MAX_FILE_BYTES,
   GPT_FILE_ACCEPT,
} from "@repo/ui/gailgpt/files";

/**
 * Get a display label from an accept() token without translations.
 * - ".pdf" -> "PDF"
 * - "image/png" -> "PNG" (resolved via MIME map)
 * - "application/vnd.openxmlformats-officedocument.wordprocessingml.document" -> "DOCX"
 * - "image/*" -> "IMAGE/*"
 */
export const getDisplayLabelFromAcceptToken = (token?: string): string => {
   if (!token || token === "*/*") return "*/*";
   const t = token.toLowerCase();

   // Wildcards are not displayed as a type label
   if (t.endsWith("/*")) return "";

   // Extension form
   if (t.startsWith(".")) return t.substring(1).toUpperCase();

   // MIME form: resolve to extension if possible
   const extWithDot = getFileExtensionFromMimeType(t);
   if (extWithDot) return extWithDot.replace(".", "").toUpperCase();

   // Fallback to showing the raw token uppercased
   return t.toUpperCase();
};

// -------------------------
// File validation helpers
// -------------------------

export type FileValidationRejection = {file: File; reasons: string[]};

export const parseAccept = (accept?: string): string[] => {
   if (!accept || accept === "*/*") return [];
   return accept
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
};

export const isAllowedByAccept = (file: File, acceptRules: string[]): boolean => {
   if (acceptRules.length === 0) return true;
   const fileType = file.type.toLowerCase();
   const fileName = file.name.toLowerCase();
   return acceptRules.some((rule) => {
      const r = rule.toLowerCase();
      if (r.startsWith(".")) return fileName.endsWith(r);
      if (r.endsWith("/*")) return fileType.startsWith(r.replace("/*", "/"));
      return fileType === r;
   });
};

export const limitFilesByMultiple = (files: File[], allowMultiple: boolean): File[] => {
   return allowMultiple ? files : files.slice(0, 1);
};

export const splitByMaxFiles = (
   files: File[],
   maxFiles: number | undefined,
   t: (key: string, options?: any) => string
): {candidates: File[]; rejected: FileValidationRejection[]} => {
   if (typeof maxFiles !== "number") return {candidates: files, rejected: []};
   const candidates = files.slice(0, maxFiles);
   const extras = files.slice(maxFiles);
   const rejected: FileValidationRejection[] = extras.map((f) => ({
      file: f,
      reasons: [t("fileUpload.tooManyFiles")],
   }));
   return {candidates, rejected};
};

export const validateSingleFile = (
   file: File,
   acceptRules: string[],
   maxFileSize: number,
   t: (key: string, options?: any) => string
): string[] => {
   const reasons: string[] = [];
   if (!isAllowedByAccept(file, acceptRules)) reasons.push(t("fileUpload.unsupportedType"));
   if (file.size > maxFileSize)
      reasons.push(
         t("fileUpload.fileTooLarge", {
            size: formatFileSize(file.size),
            max: formatFileSize(maxFileSize),
         })
      );
   return reasons;
};

export const validateFilesAgainstRules = (
   files: File[],
   acceptRules: string[],
   maxFileSize: number,
   t: (key: string, options?: any) => string
): {valid: File[]; rejected: FileValidationRejection[]} => {
   const valid: File[] = [];
   const rejected: FileValidationRejection[] = [];
   for (const file of files) {
      const reasons = validateSingleFile(file, acceptRules, maxFileSize, t);
      if (reasons.length > 0) rejected.push({file, reasons});
      else valid.push(file);
   }
   return {valid, rejected};
};

export const enforceTotalSizeConstraint = (
   validFiles: File[],
   maxTotalSize: number | undefined,
   t: (key: string, options?: any) => string
): {valid: File[]; rejected: FileValidationRejection[]} => {
   if (typeof maxTotalSize !== "number") return {valid: validFiles, rejected: []};
   let total = 0;
   const kept: File[] = [];
   const rejected: FileValidationRejection[] = [];
   for (const file of validFiles) {
      if (total + file.size <= maxTotalSize) {
         kept.push(file);
         total += file.size;
      } else {
         rejected.push({file, reasons: [t("fileUpload.totalSizeExceeded")]});
      }
   }
   return {valid: kept, rejected};
};
